// ADR 5.9.26i §3 — locale resolution, copied from the landing worker's rules
// and retargeted at the iOS file names.
//
// HSKVocabInfra/workers/landing-i18n.js is the existing, reviewed answer to
// "which of our locales does this browser want?": full-tag alias first, then
// base-strip, then null. The one substantive difference here is the target
// vocabulary. The worker routes to the marketing site's file names, where
// Simplified Chinese is `zh`; this catalogue is `ios/locales/*.json`, where the
// same language is `zh-Hans`. So every rule that lands on `zh` there lands on
// `zh-Hans` here, and the SUPPORTED list below is the directory listing of
// ios/locales, not the worker's SUPPORTED.
//
// Keep this in step with that worker. Divergence is not caught by any test the
// two repos share — they are checked out independently — so it is a review-time
// obligation, exactly as the worker's own header says of the Public site.
//
// ADR 30.9.26d §2 layers a remembered choice under `?lng=` and over the
// browser (`chooseLocale`); the worker has no such choice, so it has no twin.

/** The 31 catalogues `scripts/build-locales.mjs` emits — ios/locales' basenames. */
export const SUPPORTED_LOCALES = [
  'af', 'ar', 'ckb', 'de', 'el', 'en', 'es', 'fa', 'fi', 'fil', 'fr', 'hak',
  'hi', 'id', 'it', 'ja', 'km', 'ko', 'ku', 'ms', 'nan', 'nl', 'prs', 'ps',
  'pt', 'sw', 'tr', 'vi', 'yue', 'zh-Hans', 'zh-Hant',
] as const

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: SupportedLocale = 'en'

/**
 * Full-tag aliases, matched before the base strip.
 *
 * Each entry is the worker's, with `zh` rewritten to `zh-Hans`:
 *   - Chinese script variants, which base-strip would collapse into one;
 *   - the legacy Filipino/Kurdish tags (`tl`, `kmr`);
 *   - the Cantonese/Hokkien extlangs, which must not collapse to Chinese;
 *   - `fa-AF`, where Persian-as-used-in-Afghanistan is Dari, not Farsi.
 */
export const LOCALE_ALIASES: Readonly<Record<string, SupportedLocale>> = {
  'zh-TW': 'zh-Hant', 'zh-HK': 'zh-Hant', 'zh-Hant': 'zh-Hant',
  'zh-CN': 'zh-Hans', 'zh-Hans': 'zh-Hans', zh: 'zh-Hans',
  tl: 'fil', 'tl-PH': 'fil',
  kmr: 'ku', 'kmr-TR': 'ku',
  'zh-yue': 'yue', 'zh-min-nan': 'nan', 'zh-nan': 'nan',
  'fa-AF': 'prs',
}

/**
 * The API's language code for a catalogue locale (ADR 8.9.26o §1). Identity but
 * for Simplified Chinese, which `ios/locales` — and so `LOCALE_ALIASES` — calls
 * `zh-Hans` and `supported_languages.code` calls `zh`. Identity off-domain by
 * construction: the caller has a `string` (i18next's `language`, a `<select>`
 * value), and every other locale we ship is already a code the API serves. §3
 * pins that.
 */
export function apiLanguageCode(locale: string): string {
  return locale === 'zh-Hans' ? 'zh' : locale
}

function isSupported(tag: string): tag is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(tag)
}

/**
 * Public's path prefix for a catalogue locale (ADR 25.9.26c §5, moved here by
 * 8.10.26b §5), or null for English and for anything we ship no catalogue
 * for — both get the unprefixed page.
 *
 * The site's 31 locale codes are `SUPPORTED_LOCALES` with `zh` for `zh-Hans`,
 * which is exactly the one rewrite `apiLanguageCode` makes: the API and the
 * site both call Simplified Chinese `zh`. A future locale whose site code
 * differs from its API code would need its own mapping here.
 */
export function siteLocale(locale: string): string | null {
  if (!isSupported(locale) || locale === 'en') return null
  return apiLanguageCode(locale)
}

/** One BCP-47 tag to a catalogue, or null when we ship nothing for it. */
export function resolveLocale(tag: string): SupportedLocale | null {
  const alias = LOCALE_ALIASES[tag]
  if (alias) return alias
  const base = tag.split('-')[0]
  return isSupported(base) ? base : null
}

/**
 * The first tag we can serve, else English.
 *
 * `?lng=` beats the browser: it is how a shared link pins a language and how
 * the 31 catalogues stay reachable from one machine during review.
 */
export function pickLocale(
  preferred: readonly string[],
  override?: string | null,
): SupportedLocale {
  const tags = override ? [override, ...preferred] : preferred
  for (const tag of tags) {
    const resolved = resolveLocale(tag)
    if (resolved) return resolved
  }
  return DEFAULT_LOCALE
}

/** The browser's ordered preference list, narrowed to what a DOM actually has. */
export function browserLocales(nav: Pick<Navigator, 'languages' | 'language'> = navigator): string[] {
  return nav.languages?.length ? [...nav.languages] : [nav.language]
}

/** The `?lng=` pin, if the URL carries one. */
export function localeOverride(search: string): string | null {
  return new URLSearchParams(search).get('lng')
}

/** Why a launch resolved to its locale (ADR 30.9.26d §2) — `web_app_opened`'s `ui_locale_source`. */
export type LocaleSource = 'url' | 'stored' | 'browser' | 'fallback'

/**
 * ADR 30.9.26d §2 — `?lng=` > stored choice > `navigator.languages` > `en`.
 *
 * `stored` is the RAW stored string and is resolved here, not trusted: a removed
 * or garbage code falls through to the browser instead of stranding the learner.
 * With `stored` null the locale is exactly `pickLocale(preferred, ?lng=)`.
 */
export function chooseLocale(
  search: string,
  stored: string | null,
  preferred: readonly string[],
): { locale: SupportedLocale; source: LocaleSource } {
  const override = localeOverride(search)
  const fromUrl = override ? resolveLocale(override) : null
  if (fromUrl) return { locale: fromUrl, source: 'url' }
  const fromStore = stored ? resolveLocale(stored) : null
  if (fromStore) return { locale: fromStore, source: 'stored' }
  const fromBrowser = preferred.map(resolveLocale).find((tag) => tag !== null)
  if (fromBrowser) return { locale: fromBrowser, source: 'browser' }
  return { locale: DEFAULT_LOCALE, source: 'fallback' }
}

/**
 * ADR 30.9.26d §4 — the locale dates and prices format with: the first of
 * `[intlDefault, ...preferred]` that resolves to the app locale (`en-GB` for
 * `en`), else the app locale itself.
 *
 * `intlDefault` goes first because it is what the four sites formatted with
 * before, so anyone whose app language is the browser's keeps today's format
 * even when `navigator.languages` names another region.
 */
export function formatLocale(
  appLocale: string,
  preferred: readonly string[],
  intlDefault: string,
): string {
  return [intlDefault, ...preferred].find((tag) => resolveLocale(tag) === appLocale) ?? appLocale
}
